import { useQuery } from "@tanstack/react-query";
import { useSession } from "@/hooks/use-session";
import { supabase } from "@/lib/supabase";

/**
 * Resolves the user's current location for weather + suggestion engine.
 *
 * BW12: removed the auto-call to `navigator.geolocation`. iOS Safari/PWA
 * surfaces a permission prompt every launch when geolocation is requested
 * automatically — annoying, and never necessary because Context.location.city
 * is the canonical source. Only call browser geolocation when the user
 * explicitly clicks "Use my current location" via `requestBrowserGeolocation`.
 *
 * Resolution order:
 *  1. Active Context row with explicit GPS coords
 *  2. Active Context city → look up centroid table
 *  3. Hardcoded Zürich fallback
 */

export type Resolved = {
  lat: number;
  lon: number;
  source: "context_gps" | "context_city" | "fallback" | "browser_geo";
  label: string;
};

const CITY_CENTROIDS: Record<string, { lat: number; lon: number }> = {
  zurich: { lat: 47.3769, lon: 8.5417 },
  zürich: { lat: 47.3769, lon: 8.5417 },
  boston: { lat: 42.3601, lon: -71.0589 },
  cambridge: { lat: 42.3736, lon: -71.1097 },
  lourdes: { lat: 43.0921, lon: -0.0451 },
  paris: { lat: 48.8566, lon: 2.3522 },
  berlin: { lat: 52.52, lon: 13.405 },
  london: { lat: 51.5074, lon: -0.1278 },
  munich: { lat: 48.1351, lon: 11.582 },
  vienna: { lat: 48.2082, lon: 16.3738 },
  basel: { lat: 47.5596, lon: 7.5886 },
  geneva: { lat: 46.2044, lon: 6.1432 },
  lausanne: { lat: 46.5197, lon: 6.6323 },
  rome: { lat: 41.9028, lon: 12.4964 },
  milan: { lat: 45.4642, lon: 9.19 },
  amsterdam: { lat: 52.3676, lon: 4.9041 },
  copenhagen: { lat: 55.6761, lon: 12.5683 },
  "new york": { lat: 40.7128, lon: -74.006 },
  "san francisco": { lat: 37.7749, lon: -122.4194 },
};

const FALLBACK: Resolved = {
  lat: 47.3769,
  lon: 8.5417,
  source: "fallback",
  label: "Zürich",
};

function centroidFor(city: string | null | undefined) {
  if (!city) return null;
  return CITY_CENTROIDS[city.toLowerCase()] ?? null;
}

export function useContextLocation(): Resolved | null {
  const { session } = useSession();
  const userId = session?.user?.id;

  const ctxQuery = useQuery({
    queryKey: ["active_context", userId],
    enabled: Boolean(userId),
    queryFn: async () => {
      if (!userId) return null;
      const { data, error } = await supabase
        .from("context")
        .select("location")
        .eq("user_id", userId)
        .is("effective_to", null)
        .order("effective_from", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data?.location ?? null;
    },
    staleTime: 5 * 60 * 1000,
  });

  const ctxLoc = ctxQuery.data as
    | {
        gps?: { lat?: number; lon?: number } | null;
        city?: string | null;
        country?: string | null;
      }
    | null
    | undefined;
  const ctxCity = ctxLoc?.city ?? null;

  if (ctxLoc?.gps?.lat != null && ctxLoc?.gps?.lon != null) {
    return {
      lat: ctxLoc.gps.lat,
      lon: ctxLoc.gps.lon,
      source: "context_gps",
      label: ctxCity ?? "Saved location",
    };
  }

  const centroid = centroidFor(ctxCity);
  if (ctxCity && centroid) {
    return {
      lat: centroid.lat,
      lon: centroid.lon,
      source: "context_city",
      label: ctxCity,
    };
  }
  if (ctxCity) {
    return { ...FALLBACK, label: ctxCity };
  }
  return FALLBACK;
}

/**
 * Explicit user-initiated browser geolocation. Only call from a button
 * tap so iOS treats it as a permission flow, not an unprompted prompt.
 */
export function requestBrowserGeolocation(): Promise<Resolved | null> {
  return new Promise((resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          source: "browser_geo",
          label: "Detected location",
        }),
      () => resolve(null),
      { maximumAge: 30 * 60 * 1000, timeout: 10_000, enableHighAccuracy: false },
    );
  });
}
